import ArrowLeft from '@assets/icons/arrow-left.svg';
import BottomSheet, { BottomSheetFlatListMethods } from '@gorhom/bottom-sheet';
import distance from 'jaro-winkler';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import { BusinessInfoCardList } from '@/components/locations/business-info-card-list';
import {
  COLLAPSED_SHEET_SIZE_PERCENT,
  OPEN_SHEET_SIZE_PERCENT,
  snapPoints,
  useLeadGeneratorLocation,
} from '@/hooks/locations/use-lead-generator-location';
import { CONTENT_HEIGHT } from '@/components/navigation-header-content';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import { FilterPills } from '@/components/filter-pills';
import { FloatingBusinessInfoCard } from '@/components/locations/floating-business-info-card';
import { LeadGenerator_NearbyBusinessesQuery } from '@/types';
import { LeadGeneratorLocationSearch } from '@/components/locations/lead-generator-location-search';
import { LeadGeneratorMap } from '@/components/locations/lead-generator-map';
import { LOCATION_MANAGER_ROUTES } from '@/navigators/routes';
import { LocationManagerStackParams } from '@/navigators/types';
import { ModalContainer } from '@/components/modal-container';
import { MultipleOptionsActionSheet } from '@/components/multiple-options-action-sheet';
import { PillButton, PillButtonType } from '@/components/pill-button';
import {
  priceOptions,
  sortOptions,
  useLeadGeneratorFiltering,
} from '@/hooks/locations/use-lead-generator-filtering';
import { SingleOptionActionSheet } from '@/components/single-option-action-sheet';
import { SortOrder, useNearbyBusinesses } from '@/hooks/use-nearby-businesses';
import { StackScreenProps } from '@react-navigation/stack';
import { StyleSheet, Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useLeadGeneratorCreateAccount } from '@/hooks/locations/use-lead-generator-create-account';
import { useUserContext } from '@/contexts/user-context';

type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.LeadGenerator
>;

export const LeadGeneratorScreen: React.FC<Props> = ({ navigation }) => {
  const { isSmallLayout } = useLayoutContext();

  const { selectedRestaurantRelationship } = useUserContext();

  const [attempts, setAttempts] = useState(0);

  const {
    directHit,
    dragLeadGeneratorLocation,
    isUserLocation,
    leadGeneratorLocation,
    listSize,
    mapChanged,
    onBusinessSelect,
    onMapTouch,
    onPanDrag,
    onPositionChanged,
    onRegionChangeComplete,
    onSearchArea,
    selectCurrentLocation,
    selectedBusiness,
    selectedLocationName,
    setDirectHit,
    setLeadGeneratorLocation,
    setMapChanged,
    setSelectedBusiness,
    setSelectedLocationName,
    setShowLeadGeneratorList,
    showLeadGeneratorList,
    userLocation,
  } = useLeadGeneratorLocation();

  const {
    buttonProps,
    categories,
    categoryOptions,
    filters,
    isCurrentRestaurant,
    updateFilters,
  } = useLeadGeneratorFiltering(() => setAttempts(0));
  const {
    priceRange,
    selectedCategories,
    sortOrder,
    defaultCategories,
    showSortOrder,
    showPriceRange,
    showCategories,
  } = filters;

  // When any filters change, reset mapChanged to false
  // so that the "Search this area" button is hidden
  useEffect(() => {
    setMapChanged(false);
  }, [filters, setMapChanged]);

  const businessSelectCallback = useCallback(
    (
      nearbyBusinesses: LeadGenerator_NearbyBusinessesQuery['getNearbyBusinessesV2'],
    ) => {
      // if user has selected a location name, check nearby businesses for a direct hit
      if (selectedLocationName) {
        const foundBusiness = nearbyBusinesses?.find((business) => {
          const similarity = distance(
            business.business_name.toLowerCase().trim(),
            selectedLocationName.toLowerCase().trim(),
          );
          return similarity > 0.9;
        });

        // if direct hit we should show specific business card instead of list
        if (foundBusiness) {
          setDirectHit(true);
          setSelectedBusiness(foundBusiness);
          setShowLeadGeneratorList(false);
          return;
        }

        switch (attempts) {
          // if we miss the first time, expand the search radius
          case 0:
            setLeadGeneratorLocation((location) => ({
              ...location,
              radius: '1000',
            }));
            setAttempts((a) => a + 1);
            break;
          // if we miss the second time, reset the search radius and sort by best match
          case 1:
            setLeadGeneratorLocation((location) => ({
              ...location,
              radius: null,
            }));
            updateFilters({ sortOrder: SortOrder.BEST_MATCH });
            setAttempts((a) => a + 1);
            break;
          // if we miss the third time, there is no direct hit
          default:
            setDirectHit(false);
            setShowLeadGeneratorList(true);
            break;
        }
      }
    },
    [
      attempts,
      selectedLocationName,
      setDirectHit,
      setSelectedBusiness,
      setShowLeadGeneratorList,
      setLeadGeneratorLocation,
      updateFilters,
    ],
  );

  // find nearby businesses using location and selected filters
  const {
    error,
    fetchMore,
    fetchingMore,
    loading,
    nearbyBusinesses,
    retry,
    supplierCountryCode,
  } = useNearbyBusinesses(
    selectedRestaurantRelationship.supplierUUID,
    {
      categories,
      isCurrentRestaurant,
      latitude: leadGeneratorLocation.latitude!,
      longitude: leadGeneratorLocation.longitude!,
      price: priceRange,
      radius: leadGeneratorLocation.radius,
      sortBy: sortOrder,
    },
    businessSelectCallback,
  );

  const navigateToProspect = (uuid: string, restaurantName: string) => {
    navigation.navigate(LOCATION_MANAGER_ROUTES.ViewAccount, {
      onClose: () => {},
      restaurantName,
      restaurantUUID: uuid,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    });
  };

  const { createAccount, newAccountYelpBusinessID } =
    useLeadGeneratorCreateAccount(
      selectedRestaurantRelationship.supplierUUID,
      {
        categories,
        isCurrentRestaurant,
        latitude: leadGeneratorLocation.latitude!,
        longitude: leadGeneratorLocation.longitude!,
        price: priceRange,
        radius: leadGeneratorLocation.radius,
        sortBy: sortOrder,
      },
      (businessUUID: string, businessName: string) => {
        navigateToProspect(businessUUID, businessName);
      },
    );

  // lead generator list
  const bottomSheetRef = useRef<BottomSheet>(null);
  const flatListRef = useRef<BottomSheetFlatListMethods>(null);

  // Set bottom sheet position based on leadGeneratorListSize
  useEffect(() => {
    if (listSize === COLLAPSED_SHEET_SIZE_PERCENT) {
      bottomSheetRef.current?.snapToIndex(0);
    } else {
      bottomSheetRef.current?.snapToIndex(1);
    }
  }, [bottomSheetRef, listSize]);

  // filter pills
  const renderPillButton = useCallback((button: PillButtonType) => {
    return <PillButton button={button} key={button.label} />;
  }, []);

  return (
    <ModalContainer
      navigationHeaderProps={{
        BackButtonIcon: ArrowLeft,
        showBackButton: isSmallLayout,
        showShadow: true,
        subheader:
          leadGeneratorLocation.latitude && leadGeneratorLocation.longitude ? (
            <>
              <LeadGeneratorLocationSearch
                selectCurrentLocation={() => {
                  setAttempts(0);
                  selectCurrentLocation();
                }}
                selectedLocationName={selectedLocationName}
                selectLocation={(coordinates, radius, sortOrder) => {
                  setAttempts(0);
                  setLeadGeneratorLocation({
                    latitude: coordinates.latitude,
                    longitude: coordinates.longitude,
                    radius: radius,
                  });
                  updateFilters({ sortOrder: sortOrder });
                }}
                setMapChanged={setMapChanged}
                setSelectedBusiness={setSelectedBusiness}
                setSelectedLocationName={(name?: string) => {
                  setSelectedLocationName(name);
                }}
                setShowLeadGeneratorList={setShowLeadGeneratorList}
              />
              <FilterPills
                buttons={buttonProps}
                renderPillButton={renderPillButton}
              />
            </>
          ) : null,
        title: i18n.t('lead_generator'),
      }}
    >
      {leadGeneratorLocation.latitude && leadGeneratorLocation.longitude ? (
        <>
          <LeadGeneratorMap
            collapsedSheetSizePercent={COLLAPSED_SHEET_SIZE_PERCENT}
            directHit={directHit}
            dragLeadGeneratorLocation={dragLeadGeneratorLocation}
            leadGeneratorLocation={leadGeneratorLocation as Coordinate}
            mapChanged={mapChanged}
            nearbyBusinesses={nearbyBusinesses}
            onBusinessSelect={onBusinessSelect}
            onMapTouch={onMapTouch}
            onPanDrag={onPanDrag}
            onRegionChangeComplete={onRegionChangeComplete}
            onSearchArea={() => {
              setAttempts(0);
              onSearchArea();
            }}
            selectedBusiness={selectedBusiness}
            userLocation={userLocation}
          />
          {selectedBusiness ? (
            <FloatingBusinessInfoCard
              business={selectedBusiness}
              createAccount={createAccount}
              navigateToProspect={navigateToProspect}
              newAccountYelpBusinessID={newAccountYelpBusinessID}
              supplierCountryCode={supplierCountryCode}
            />
          ) : null}
          {!selectedBusiness ? (
            <BusinessInfoCardList
              bottomSheetRef={bottomSheetRef}
              collapsedContainerHeight={COLLAPSED_SHEET_SIZE_PERCENT}
              createAccount={createAccount}
              dragLeadGeneratorLocation={dragLeadGeneratorLocation}
              error={error}
              fetchingMore={fetchingMore}
              fetchMore={fetchMore}
              flatListRef={flatListRef}
              isCollapsed={listSize === COLLAPSED_SHEET_SIZE_PERCENT}
              isUserLocation={isUserLocation}
              isVisible={showLeadGeneratorList}
              loading={loading}
              navigateToProspect={navigateToProspect}
              nearbyBusinesses={nearbyBusinesses}
              newAccountYelpBusinessID={newAccountYelpBusinessID}
              onPositionChanged={onPositionChanged}
              openContainerHeight={OPEN_SHEET_SIZE_PERCENT}
              retry={retry}
              selectCurrentLocation={() => {
                setAttempts(0);
                selectCurrentLocation();
              }}
              snapPoints={snapPoints}
              supplierCountryCode={supplierCountryCode}
            />
          ) : null}
          {showSortOrder ? (
            <SingleOptionActionSheet<SortOrder>
              initialSelectedValue={sortOrder}
              onApplyChanges={(value) => updateFilters({ sortOrder: value })}
              onClose={() => updateFilters({ showSortOrder: false })}
              options={sortOptions}
              title={i18n.t('sort')}
            />
          ) : null}
          {showPriceRange ? (
            <SingleOptionActionSheet
              initialSelectedValue={priceRange}
              onApplyChanges={(value) => {
                setAttempts(0);
                updateFilters({ priceRange: value });
              }}
              onClose={() => updateFilters({ showPriceRange: false })}
              options={priceOptions}
              title={i18n.t('price_range')}
            />
          ) : null}
          {showCategories ? (
            <MultipleOptionsActionSheet
              defaultSelectedOptions={defaultCategories.selectedCategories}
              defaultSettingsActive={defaultCategories.enabled}
              initialSelectedOptions={selectedCategories}
              onApplyChanges={(selectedCategories) => {
                setAttempts(0);
                updateFilters({
                  selectedCategories,
                });
                Analytics.track(
                  'lead_generator.categories_filter.apply_changes',
                  {
                    categories: Array.from(selectedCategories),
                    supplierUUID: selectedRestaurantRelationship.supplierUUID,
                  },
                );
              }}
              onApplyDefaultChanges={(selectedCategories) => {
                updateFilters({
                  defaultCategories: {
                    ...defaultCategories,
                    selectedCategories,
                  },
                });
                Analytics.track(
                  'lead_generator.categories_filter.apply_default_changes',
                  {
                    categories: Array.from(selectedCategories),
                    supplierUUID: selectedRestaurantRelationship.supplierUUID,
                  },
                );
              }}
              onClose={() => updateFilters({ showCategories: false })}
              options={categoryOptions}
              setDefaultSettingsActive={(enabled) =>
                updateFilters({
                  defaultCategories: { ...defaultCategories, enabled },
                })
              }
              title={i18n.t('categories')}
            />
          ) : null}
        </>
      ) : (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingCopy}>
            {i18n.t('lead_generator.loading')}
          </Text>
          <ActivityIndicator size="large" />
        </View>
      )}
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    alignItems: 'center',
    bottom: 40 + CONTENT_HEIGHT / 2,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  loadingCopy: {
    ...TYPOGRAPHY.h4,
    marginBottom: 40,
  },
});
